import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import * as _ from 'lodash-es';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { TaxLiabilityDashboardService } from './tax-liability-dashboard.service';
import { DatePipe } from '@angular/common';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { finalize, Observable, Subscription } from 'rxjs';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-tax-liability-dashboard',
    templateUrl: './tax-liability-dashboard.component.html',
    styleUrls: ['./tax-liability-dashboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TaxLiabilityDashboardComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // 🔴 V9: Flags to prevent duplicate initialization
  private isInitialized = false;
  private isLoading = false;
  private readonly contextSubs = new Subscription();
  private contextEpoch = 0;

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;
  dataLoading = false;

  // 🚀 CRITICAL: Filter params input for dynamic data
  filterParams: any = {};
  baseURLs: any = {};
  rowData: any = {};

  @Input('grid-data')
  set _gridData(gridData: string){
    try{
      this.gridData = safeJsonInput<any>(gridData, null, 'tax-liability-dashboard.parse1');
      this.tryInit();
    }catch(e){
      console.log("Grid data input not yet stable. Still waiting to get resolved.");
    }
  }
  gridData: any = {};

  @Input('modal-name')
  set _modal_name(modalName: string){
    try{
      this.modal_name = modalName;
      this.tryInit();
    }catch(e){
      console.log("Modal name input not yet stable. Still waiting to get resolved.");
    }
  }

  // Component properties
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};

  // Modal properties
  modalTitle = "Change Status";
  title = "Tax Liability Amount";
  modal_name: string | null = null;
  
  // Entity properties
  entities: any[] = [];
  
  // Status properties
  radioUnblk: string = 'Y';
  statusFlag: boolean = false;
  
  // Form data properties
  editRecords: any[] = [];
  originalEditRecords: any[] = []; // Store original editRecords for reset
  editTotalRecords: any[] = [];
  objects: any[] = [];
  
  // Cached filtered records to avoid repeated filtering on every change detection
  private _filteredRecords: any[] = [];
  private _filteredTotalRecords: any[] = [];
  selectedGroup: any = {};
  currencies: any[] = [];
  description: any[] = [];
  adjustment: any[] = [];
  ftrRelatesToList: any[] = [];
  descriptionDateProvided: any[] = [];
  
  // Calendar properties
  calendar: { opened: boolean[] } = { opened: [] };
  editcalendar: { opened: boolean[] } = { opened: [] };
  
  // Other properties
  taxesAmount: any = null;
  
  // Confirmation modal properties
  showConfirmDialog: boolean = false;
  modalConfig: ConfirmationModalConfig = {
    title: '',
    message: ''
  };
  // Store delete parameters temporarily for confirmation
  private pendingDeleteParams: { response: any; isDelete: boolean; index: number } | null = null;
  // Store edit parameters temporarily for confirmation
  private pendingEditParams: { editData: any; isDelete: boolean; index: number } | null = null;
  
  // Public getter to check if delete action is pending (for template access)
  get hasPendingDelete(): boolean {
    return this.pendingDeleteParams !== null;
  }
  taxableType: string = '';
  currencyType: string = 'FC';
  editList: any[] = [];
  addNewFlag: boolean = false;
  resetFlag: boolean = false;
  disableTable: boolean = false;
  showData: any = null;
  currOverrideFlag: string = '';
  flag1: boolean = false;
  OnLoadObjData: any[] = [];
  filterLength: number = 0; // Used for row ID generation
  
  // API call tracking for dataLoading
  private pendingApiCalls: number = 0;
  private isDataLoadingActive: boolean = false;

  /** Incremented when tax-liability primary load starts; stale callbacks are ignored */
  private taxLiabilityLoadSeq = 0;

  /**
   * Custom RxJS operator to track API calls for dataLoading state
   * Automatically increments counter on subscribe and decrements on complete/error
   */
  private trackLoading<T>(source: Observable<T>): Observable<T> {
    return new Observable(observer => {
      if (this.isDataLoadingActive) {
        this.pendingApiCalls++;
      }
      
      const subscription = source.pipe(
        finalize(() => {
          if (this.isDataLoadingActive) {
            this.pendingApiCalls--;
            if (this.pendingApiCalls <= 0) {
              this.dataLoading = false;
              this.isDataLoadingActive = false;
              this.pendingApiCalls = 0;
              this.cdr.detectChanges();
            }
          }
        })
      ).subscribe(observer);
      
      return subscription;
    });
  }
  
  // Validation flags
  descRowFlag: boolean = false;
  adjustRowFlag: boolean = false;
  dateRowFlag: boolean = false;
  dateFlagOpen: boolean = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private jsonObjectFactory: JsonObjectFactoryService,
    private taxLiabilityDashboardService: TaxLiabilityDashboardService,
    private datePipe: DatePipe,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) { }

  ngOnInit(): void {
    this.resetAndTryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.resetAndTryInit();
      })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.resetAndTryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.contextEpoch++;
    this.contextSubs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? '',
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    this.USER_SETTINGS = this.gtwApp?.USER_SETTINGS || {};
    this.SERVER_CONFIG = this.gtwApp?.SERVER_CONFIG || {};
    const modalFromGp = extractObject(mergedGp).modalScreen?.modal_name
      || activeScreen?.data?.modal_name
      || activeScreen?.data?.modalName;
    if (modalFromGp) {
      this.modal_name = modalFromGp;
    }
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
    Object.assign(this.globalService, mergedGp);
  }

  private resetAndTryInit(): void {
    this.contextEpoch++;
    this.isInitialized = false;
    this.isLoading = false;
    this.applyWorkspaceContext();
    this.tryInit();
    this.cdr.markForCheck();
  }

  // 🔴 V9: Gate function to prevent duplicate initialization
  private tryInit() {
    if (this.isInitialized || this.isLoading) {
      return;
    }
    
    // Check if ALL required data is present before initializing
    // For change status mode: gridData is required, rowData is not required
    // For tax liability mode: rowData is required
    if (!this.globalService || !this.GENERAL_CONFIG || !this.filterParams) {
      return;
    }
    if (!this.getBaseUrl()) {
      return;
    }
    
    // Check modal_name to determine which data is required
    if (this.modal_name === 'changeStatusTlc') {
      // Change Status Mode: gridData is required, rowData is optional
      if (!this.gridData || (typeof this.gridData === 'object' && Object.keys(this.gridData).length === 0)) {
        return;
      }
    } else {
      // Tax Liability Mode: rowData and gridData are required
      if (!this.rowData || Object.keys(this.rowData).length === 0) {
        return;
      }
      if (!this.gridData || (typeof this.gridData === 'object' && Object.keys(this.gridData).length === 0)) {
        return;
      }
    }
    
    this.isLoading = true;
    this.init();
  }

  private getBaseUrl(): string {
    return (this.GENERAL_CONFIG && (this.GENERAL_CONFIG.base_url || this.GENERAL_CONFIG.api_url)) || '';
  }

  init() {
    try {
      this.dataLoading = true;
      
      // Check modal name to determine mode
      if (this.modal_name === 'changeStatusTlc') {
        this.initChangeStatusMode();
      } else {
        this.initTaxLiabilityMode();
      }
    } catch (e) {
      console.error('Error in component init:', e);
      this.showFormLoader = false;
      this.dataLoading = false;
      this.isLoading = false;
      this.isInitialized = false;
      this.cdr.detectChanges();
    }
  }

  initChangeStatusMode() {
    // Handle different gridData structures
    let gridDataArray: any[] = [];
    
    if (this.gridData) {
      // Check if gridData is an array
      if (Array.isArray(this.gridData) && this.gridData.length > 0) {
        gridDataArray = this.gridData;
      }
      // Check if gridData has a data property that's an array
      else if (this.gridData.data && Array.isArray(this.gridData.data) && this.gridData.data.length > 0) {
        gridDataArray = this.gridData.data;
      }
      // Check if gridData has a rows property that's an array
      else if (this.gridData.rows && Array.isArray(this.gridData.rows) && this.gridData.rows.length > 0) {
        gridDataArray = this.gridData.rows;
      }
    }
    
    // Initialize entities
    if (gridDataArray.length > 0) {
      this.entities = _.clone(gridDataArray);
      
      // Get status for first entity
      if (this.entities.length > 0) {
        const firstEntity = this.entities[0].data || this.entities[0];
        const tax_year = firstEntity.TAX_YEAR;
        const combination_key = firstEntity.COMBINATION_KEY;
        const status = firstEntity.STATUS;
        
        this.taxLiabilityDashboardService.getStatus(
          tax_year,
          combination_key,
          this.getBaseUrl(),
          this.globalService,
          this.filterParams,
          this.rowData
        ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
          (result: any) => {
            this.radioUnblk = status || 'Y';
            this.dataLoading = false;
            this.showFormLoader = false;
            this.isLoading = false;
            this.isInitialized = true;
            this.cdr.detectChanges();
          },
          (error: any) => {
            console.error('Error loading status:', error);
            this.dataLoading = false;
            this.showFormLoader = false;
            this.isLoading = false;
            this.isInitialized = true;
            this.cdr.detectChanges();
          }
        );
      } else {
        this.dataLoading = false;
        this.showFormLoader = false;
        this.isLoading = false;
        this.isInitialized = true;
        this.cdr.detectChanges();
      }
    } else {
      // No grid data available, but still initialize
      this.entities = [];
      this.dataLoading = false;
      this.showFormLoader = false;
      this.isLoading = false;
      this.isInitialized = true;
      this.cdr.detectChanges();
    }
  }

  initTaxLiabilityMode() {
    this.title = 'Tax Liability Amount';
    this.editRecords = [];
    
    if (!this.rowData.STATUS || this.rowData.STATUS == 'Open') {
      this.rowData.STATUS = 'Open';
      this.statusFlag = false;
    } else {
      this.statusFlag = true;
    }
    
    this.getModelPopup();
  }

  getModelPopup() {
    const seq = ++this.taxLiabilityLoadSeq;
    const transTypeKey = this.getTransTypeKey();
    if (transTypeKey !== null) {
      this.showData = transTypeKey;
    }
    this.currOverrideFlag = this.rowData.CURR_OVERIDE_FLAG;

    this.taxLiabilityDashboardService.getTaxLiability(
      this.rowData.TAX_YEAR,
      this.rowData.COMBINATION_KEY,
      this.rowData.GROUP_ID || 0,
      this.getBaseUrl(),
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (seq !== this.taxLiabilityLoadSeq) {
          return;
        }
        // Extract jsonObject from response (matches pattern used in getCurrencies, getDescription, etc.)
        // Handle cases where response.jsonObject is null, undefined, or response structure is different
        // Check callSuccess first (matches pattern in getCurrencies line 453)
        let responseData = null;
        if (response && response.callSuccess === "1") {
          // jsonObject can be null, undefined, or an array
          responseData = response.jsonObject || null;
        } else if (response && Array.isArray(response)) {
          // Fallback: if response is already an array (direct structure)
          responseData = response;
        }
        // responseData will be null if jsonObject is null, callSuccess !== "1", or response is invalid
        this.processTaxLiabilityResponse(responseData, transTypeKey);
      },
      (error: any) => {
        if (seq !== this.taxLiabilityLoadSeq) {
          return;
        }
        this.handleTaxLiabilityError(error);
      }
    );
  }

  private getTransTypeKey(): any {
    if (!this.gridData?.colData) {
      return null;
    }

    const colMapKey = this.gridData.colData.map;
    if (this.gridData.rowData) {
      const transTypeKey = this.gridData.rowData[colMapKey + '_TRANS_TYPE_KEY'];
      return transTypeKey !== undefined && transTypeKey !== null ? transTypeKey : colMapKey;
    }

    return colMapKey;
  }

  private processTaxLiabilityResponse(response: any, transTypeKey: any): void {
    // Handle null/undefined response or null jsonObject (matches original JS line 209 check)
    // response can be: null, undefined, empty array, or array of objects
    const responseArray = (response && Array.isArray(response)) ? response : null;
    const filteredEntities = responseArray ? _.filter(responseArray, { TRANS_TYPE_KEY: transTypeKey }) : [];
    // Initialize filterLength with filtered entities count (matches original JS line 206)
    this.filterLength = filteredEntities === undefined ? 0 : filteredEntities.length;
    const minRows = 5;

    // Check if response exists and is a valid array (matches original JS line 209: if(response))
    if (responseArray && responseArray.length > 0) {
      this.editRecords = responseArray;
      // Process response records and increment filterLength for each (matches original JS lines 212-219)
      this.processResponseRecords(responseArray);
      this.resetObjectsIfNeeded();
      // Load currencies and dropdowns - mapDropdownValues() will be called after all dropdowns are loaded in getCurrencies()
      this.getCurrencies();
      // Refresh filtered records after data is populated
      this.refreshFilteredRecords();
    } else {
      // Handle null/undefined/empty response (matches original JS lines 237-261)
      this.disableTable = true;
      this.resetObjectsIfNeeded();
      this.getCurrDefault();
      // Clear filtered records when no data
      this.refreshFilteredRecords();
    }

    // Initialize new rows if resetFlag is undefined (matches original JS lines 262-270)
    if (this.resetFlag === undefined || !this.resetFlag) {
      this.initializeNewRows(minRows);
      this.syncDatePickerArrays();
      this.OnLoadObjData = _.cloneDeep(this.objects);
    } else {
      this.syncDatePickerArrays();
    }

    this.initializeApiTracking();
    this.showFormLoader = false;
    this.isLoading = false;
    this.isInitialized = true;
    this.cdr.detectChanges();
  }

  /** Keep AppKit dpSelectedDates arrays in sync with row date values. */
  private syncDatePickerArrays(): void {
    (this.objects || []).forEach((row: any) => {
      row.dateFiledDates = this.toDateArray(row?.dateFiled) || [];
    });
    (this.editRecords || []).forEach((row: any) => {
      row.transDateDates = this.toDateArray(row?.TRANS_DATE) || [];
    });
  }

  private processResponseRecords(response: any[]): void {
    // Process each response record and increment filterLength (matches original JS lines 212-219)
    response.forEach((record: any) => {
      if (record.TYPE === 'STANDALONE') {
        // Convert TRANS_DATE to Date object for proper formatting in date picker (MM/DD/yyyy)
        // Handle both string and Date inputs, ensure valid Date object
        if (record.TRANS_DATE) {
          // Handle string dates (common formats: "YYYY-MM-DD", "MM/DD/YYYY", ISO format)
          // 🔴 CRITICAL: Parse dates as local dates to avoid timezone shifts
          let dateValue: Date;
          if (typeof record.TRANS_DATE === 'string') {
            // If date string is in format "YYYY-MM-DD", parse it as local date (not UTC)
            if (record.TRANS_DATE.match(/^\d{4}-\d{2}-\d{2}/)) {
              const parts = record.TRANS_DATE.split('-');
              dateValue = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
            }
            // Handle MM/DD/yyyy format
            else if (record.TRANS_DATE.match(/^\d{2}\/\d{2}\/\d{4}/)) {
              const parts = record.TRANS_DATE.split('/');
              dateValue = new Date(parseInt(parts[2]), parseInt(parts[0]) - 1, parseInt(parts[1]));
            }
            // For other string formats, try to parse as local date
            else {
              // Try to extract date components from string to avoid UTC interpretation
              const tempDate = new Date(record.TRANS_DATE);
              if (!isNaN(tempDate.getTime())) {
                // Use local date components to avoid timezone shift
                dateValue = new Date(tempDate.getFullYear(), tempDate.getMonth(), tempDate.getDate());
              } else {
                dateValue = tempDate;
              }
            }
          } else if (record.TRANS_DATE instanceof Date) {
            // Use local date components to avoid timezone shift
            dateValue = new Date(record.TRANS_DATE.getFullYear(), record.TRANS_DATE.getMonth(), record.TRANS_DATE.getDate());
          } else {
            // For other types, try to convert to Date first, then use local components
            const tempDate = new Date(record.TRANS_DATE);
            if (!isNaN(tempDate.getTime())) {
              dateValue = new Date(tempDate.getFullYear(), tempDate.getMonth(), tempDate.getDate());
            } else {
              dateValue = tempDate;
            }
          }
          
          // Only assign if it's a valid date
          if (!isNaN(dateValue.getTime())) {
            record.TRANS_DATE = dateValue;
            record.transDateDates = this.toDateArray(dateValue);
          } else {
            // If invalid, set to null so date picker can handle it
            record.TRANS_DATE = null;
            record.transDateDates = [];
          }
        } else {
          record.TRANS_DATE = null;
          record.transDateDates = [];
        }
      }
      record.EDIT_FLAG = true;
      // Increment filterLength for each response item (matches original JS line 216)
      this.filterLength = this.filterLength + 1;
    });
  }
  
  // Map dropdown values to ensure they match dropdown options after reset
  private mapDropdownValues(): void {
    // Only map if editRecords exist and dropdown arrays are loaded
    if (!this.editRecords || this.editRecords.length === 0) {
      return;
    }
    
    // Check if required dropdown arrays are loaded (adjustment is optional, only needed if statusFlag is true)
    const hasDescription = this.description && this.description.length > 0;
    const hasFTRRelatesToList = this.ftrRelatesToList && this.ftrRelatesToList.length > 0;
    const hasDescriptionDateProvided = this.descriptionDateProvided && this.descriptionDateProvided.length > 0;
    const hasAdjustment = this.adjustment && this.adjustment.length > 0;
    
    // If essential dropdowns aren't loaded yet, wait for them to load
    // This will be called again when dropdowns are loaded
    if (!hasDescription || !hasFTRRelatesToList || !hasDescriptionDateProvided) {
      return;
    }
    
    // Map dropdown values for each record
    this.editRecords.forEach((record: any) => {
      if (record.TYPE === 'STANDALONE') {
        // Map DESCRIPTION - ensure string value matches dropdown option
        // CRITICAL: Only map if value exists - don't reset to null/placeholder
        if (record.DESCRIPTION && String(record.DESCRIPTION).trim() !== '') {
          // Trim and normalize the value
          const descValue = String(record.DESCRIPTION).trim();
          // Find matching option in description array
          const matchedDesc = this.description.find((desc: any) => {
            const optionValue = String(desc.CODE_DESC || '').trim();
            return optionValue === descValue;
          });
          // Ensure the value is set correctly (dropdown uses [value] binding with strings)
          if (matchedDesc) {
            record.DESCRIPTION = matchedDesc.CODE_DESC;
          } else {
            // Keep the original value if it exists but doesn't match (don't reset to null)
            record.DESCRIPTION = descValue;
          }
        }
        // If DESCRIPTION is null/empty, leave it as is (don't set to placeholder)
        
        // Map FTRRELATESTODESC - ensure string value matches dropdown option
        // CRITICAL: Only map if value exists - don't reset to null/placeholder
        if (record.FTRRELATESTODESC && String(record.FTRRELATESTODESC).trim() !== '') {
          const ftrValue = String(record.FTRRELATESTODESC).trim();
          const matchedFTR = this.ftrRelatesToList.find((ftr: any) => {
            const optionValue = String(ftr.CODE_DESC || '').trim();
            return optionValue === ftrValue;
          });
          if (matchedFTR) {
            record.FTRRELATESTODESC = matchedFTR.CODE_DESC;
          } else {
            // Keep the original value if it exists but doesn't match (don't reset to null)
            record.FTRRELATESTODESC = ftrValue;
          }
        }
        // If FTRRELATESTODESC is null/empty, leave it as is (don't set to placeholder)
        
        // Map DESCRIPTIONDATEPROVIDEDDESC - ensure string value matches dropdown option
        // CRITICAL: Only map if value exists - don't reset to null/placeholder
        if (record.DESCRIPTIONDATEPROVIDEDDESC && String(record.DESCRIPTIONDATEPROVIDEDDESC).trim() !== '') {
          const dateDescValue = String(record.DESCRIPTIONDATEPROVIDEDDESC).trim();
          const matchedDateDesc = this.descriptionDateProvided.find((desc: any) => {
            const optionValue = String(desc.CODE_DESC || '').trim();
            return optionValue === dateDescValue;
          });
          if (matchedDateDesc) {
            record.DESCRIPTIONDATEPROVIDEDDESC = matchedDateDesc.CODE_DESC;
          } else {
            // Keep the original value if it exists but doesn't match (don't reset to null)
            record.DESCRIPTIONDATEPROVIDEDDESC = dateDescValue;
          }
        }
        // If DESCRIPTIONDATEPROVIDEDDESC is null/empty, leave it as is (don't set to placeholder)
        
        // Map ADJUSTMENT - ensure string value matches dropdown option (only if statusFlag is true)
        // CRITICAL: Only map if value exists - don't reset to null/placeholder
        if (record.ADJUSTMENT && String(record.ADJUSTMENT).trim() !== '' && this.statusFlag && hasAdjustment) {
          const adjValue = String(record.ADJUSTMENT).trim();
          const matchedAdj = this.adjustment.find((adj: any) => {
            const optionValue = String(adj.CODE_DESC || '').trim();
            return optionValue === adjValue;
          });
          if (matchedAdj) {
            record.ADJUSTMENT = matchedAdj.CODE_DESC;
          } else {
            // Keep the original value if it exists but doesn't match (don't reset to null)
            record.ADJUSTMENT = adjValue;
          }
        }
        // If ADJUSTMENT is null/empty, leave it as is (don't set to placeholder)
      }
    });
    
    // 🔴 CRITICAL: Store original editRecords after dropdown mapping is complete
    // This ensures reset can restore the exact state when modal was opened
    if (this.originalEditRecords.length === 0) {
      // Only store once - when data is first loaded and mapped
      this.originalEditRecords = _.cloneDeep(this.editRecords);
    }
    
    this.cdr.detectChanges();
  }

  private resetObjectsIfNeeded(): void {
    if (this.objects.length && this.resetFlag === true) {
      this.objects = _.cloneDeep(this.OnLoadObjData);
      this.syncDatePickerArrays();
    }
  }

  private initializeNewRows(minRows: number): void {
    // Add new rows and increment filterLength for each (matches original JS lines 263-267)
    for (let i = 0; i < minRows; i++) {
      this.filterLength = this.filterLength + 1;
      this.addNewFlag = true;
      this.addNew({});
    }
  }

  private initializeApiTracking(): void {
    this.isDataLoadingActive = true;
    this.pendingApiCalls = 0;
  }

  private handleTaxLiabilityError(error: any): void {
    console.error('Error loading tax liability data:', error);
    this.isDataLoadingActive = false;
    this.pendingApiCalls = 0;
    this.dataLoading = false;
    this.showFormLoader = false;
    this.isLoading = false;
    this.isInitialized = false;
    this.cdr.detectChanges();
  }

  getCurrencies() {
    const params = {
      "tax_year": this.filterParams?.tax_year || this.globalService?.tax_year,
      "jcd_key": this.rowData.JCD_KEY || this.globalService?.jcd_key || 250
    };

    this.trackLoading(
      this.jsonObjectFactory.getJSONObj(
        this.getBaseUrl() + "/loadJsonObject?action_code=3ucwjb",
        params,
        this.globalService,
        null,
        null
      )
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === "1") {
          this.currencies = data.jsonObject;
          this.getExisitngCurr();
          this.getDescription();
          this.getFTRDescription();
          this.getDateProvidedDescription();
          // Map dropdown values after all dropdown data is loaded
          this.mapDropdownValues();
        }
      },
      (error: any) => {
        console.error('Error loading currencies:', error);
      }
    );
  }

  getCurrDefault() {
    const params = {
      "tax_year": this.filterParams?.tax_year || this.globalService.tax_year,
      "jcd_key": this.rowData.JCD_KEY || this.globalService.jcd_key || 250
    };

    this.trackLoading(
      this.jsonObjectFactory.getJSONObj(
        this.getBaseUrl() + "/loadJsonObject?action_code=3ucwjb",
        params,
        this.globalService,
        null,
        null
      )
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === "1") {
          this.currencies = data.jsonObject;
          this.getcurrencyDefault();
          this.getDescription();
          this.getFTRDescription();
          this.getDateProvidedDescription();
          // Map dropdown values after all dropdown data is loaded
          this.mapDropdownValues();
        }
      },
      (error: any) => {
        console.error('Error loading default currencies:', error);
      }
    );
  }

  getcurrencyDefault() {
    const defaultCurr = this.rowData.DERIVED_CURR_LE;
    this.currencies.forEach((mg: any) => {
      if (defaultCurr == mg.CURR_CODE) {
        this.selectedGroup = mg;
      }
    });
  }

  getExisitngCurr() {
    this.currencies.forEach((hg: any) => {
      if (this.editRecords[0] && this.editRecords[0].CURRENCY == hg.CURR_CODE) {
        this.selectedGroup = hg;
      }
    });
    
    this.trackLoading(
      this.taxLiabilityDashboardService.getTotalTaxLiability(
        this.rowData.TAX_YEAR,
        this.rowData.COMBINATION_KEY,
        this.rowData.GROUP_ID || 0,
        this.getBaseUrl(),
        this.globalService,
        this.filterParams,
        this.rowData
      )
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        if (result) {
          this.editTotalRecords = result.jsonObject;
          // Refresh filtered records after total records are populated
          this.refreshFilteredRecords();
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading total tax liability:', error);
        this.cdr.detectChanges();
      }
    );
  }

  getDescription() {
    // 🚀 CRITICAL: Build params with all required fields for action code 'g9iudn'
    const params: any = {
      "activity_key": this.filterParams?.activity_key || this.globalService?.activity_key || '6507',
      "combination_key": this.rowData.COMBINATION_KEY || '',
      "filing_key": this.filterParams?.filing_key || this.globalService?.filing_key  || this.rowData?.FILING_KEY || '1',
      "is_issue_key_enabled": this.filterParams?.is_issue_key_enabled || this.globalService?.is_issue_key_enabled || 'N',
      "is_locked": this.filterParams?.is_locked || this.globalService?.is_locked || 'N',
      "issue_id": this.filterParams?.issue_id || this.globalService?.issue_id || '',
      "issue_id_for_tx_save": this.filterParams?.issue_id_for_tx_save || this.globalService?.issue_id_for_tx_save || '',
      "issue_short_desc": this.filterParams?.issue_short_desc || this.globalService?.issue_short_desc ||  '',
      "jcd_key": this.filterParams?.jcd_key || this.globalService?.jcd_key  || '250',
      "jcd_ta_key": this.filterParams?.jcd_ta_key || this.globalService?.jcd_ta_key || '5347',
      "parent_me_string": this.filterParams?.parent_me_string || this.globalService?.parent_me_string,
      "scenario": this.filterParams?.scenario || this.globalService?.scenario || '74',
      "scenarioCode": this.filterParams?.scenarioCode || this.filterParams?.scenario_code || this.globalService?.scenarioCode || 'RAF',
      "screen_key": this.filterParams?.screen_key || this.globalService?.screen_key || '10740',
      "screen_type": this.filterParams?.screen_type || this.globalService?.screen_type || 'grid',
      "tax_year": this.filterParams?.tax_year || this.globalService?.tax_year || ''
    };

    this.trackLoading(
      this.jsonObjectFactory.getJSONObj(
        this.getBaseUrl() + "/loadJsonObject?action_code=g9iudn",
        params,
        this.globalService,
        this.filterParams,
        this.rowData
      )
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === "1") {
          this.description = data.jsonObject;
          this.getAdjustment();
          // Map dropdown values after description is loaded
          this.mapDropdownValues();
        }
      },
      (error: any) => {
        console.error('Error loading description:', error);
      }
    );
  }

  getAdjustment() {
    // 🚀 CRITICAL: Build params with all required fields for action code '172kn4'
    const params: any = {
      "tax_year": this.filterParams?.tax_year || this.globalService?.tax_year || '',
      "scenario": this.filterParams?.scenario || this.globalService?.scenario || '',
      "jcd_key": this.filterParams?.jcd_key || this.globalService?.jcd_key || '',
      "screen_key": this.filterParams?.screen_key || this.globalService?.screen_key || '',
      "jcd_ta_key": this.filterParams?.jcd_ta_key || this.globalService?.jcd_ta_key || '',
      "activity_key": this.filterParams?.activity_key || this.globalService?.activity_key || '',
      "screen_type": this.filterParams?.screen_type || this.globalService?.screen_type || 'grid',
      "is_locked": this.filterParams?.is_locked || this.globalService?.is_locked || 'N',
      "issue_id": this.filterParams?.issue_id || this.globalService?.issue_id || '',
      "issue_short_desc": this.filterParams?.issue_short_desc || this.globalService?.issue_short_desc || '',
      "issue_id_for_tx_save": this.filterParams?.issue_id_for_tx_save || this.globalService?.issue_id_for_tx_save || '',
      "scenarioCode": this.filterParams?.scenarioCode || this.filterParams?.scenario_code || this.globalService?.scenarioCode || this.globalService?.scenario_code || 'RAF',
      "is_issue_key_enabled": this.filterParams?.is_issue_key_enabled || this.globalService?.is_issue_key_enabled || 'N',
      "filing_key": this.filterParams?.filing_key || this.globalService?.filing_key || this.rowData?.FILING_KEY || this.rowData?.CONSOL_GROUP_KEY || '',
      "parent_me_string": this.filterParams?.parent_me_string || this.globalService?.parent_me_string || ''
    };

    this.trackLoading(
      this.jsonObjectFactory.getJSONObj(
        this.getBaseUrl() + "/loadJsonObject?action_code=172kn4",
        params,
        this.globalService,
        this.filterParams,
        this.rowData
      )
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === "1") {
          this.adjustment = data.jsonObject;
          // Map dropdown values after adjustment is loaded
          this.mapDropdownValues();
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading adjustment:', error);
        this.cdr.detectChanges();
      }
    );
  }

  getFTRDescription() {
    const params = {
      "tax_year": this.filterParams?.tax_year || this.globalService.tax_year
    };

    this.trackLoading(
      this.jsonObjectFactory.getJSONObj(
        this.getBaseUrl() + "/loadJsonObject?action_code=r7gm1q",
        params,
        this.globalService,
        null,
        null
      )
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === "1") {
          this.ftrRelatesToList = data.jsonObject;
          // Map dropdown values after FTR description is loaded
          this.mapDropdownValues();
        }
      },
      (error: any) => {
        console.error('Error loading FTR description:', error);
      }
    );
  }

  getDateProvidedDescription() {
    const params = {
      "tax_year": this.filterParams?.tax_year || this.globalService?.tax_year
    };

    this.trackLoading(
      this.jsonObjectFactory.getJSONObj(
        this.getBaseUrl() + "/loadJsonObject?action_code=xri7tl",
        params,
        this.globalService,
        null,
        null
      )
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data.callSuccess === "1") {
          this.descriptionDateProvided = data.jsonObject;
          // Map dropdown values after date provided description is loaded
          this.mapDropdownValues();
        }
      },
      (error: any) => {
        console.error('Error loading date provided description:', error);
      }
    );
  }

  changeStatus(status: string) {
    this.radioUnblk = status;
  }

  saveStatus() {
    const finalData: any[] = [];
    if (this.entities.length) {
      for (let i = 0; i < this.entities.length; i++) {
        finalData.push({
          combination_key: this.entities[i].data.COMBINATION_KEY,
          status: this.radioUnblk
        });
      }
    }
    
    this.crudLoading = true;
    this.isSaveClicked = true;
    this.cdr.detectChanges();
    
    this.taxLiabilityDashboardService.saveStatus(
      finalData,
      this.getBaseUrl(),
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;

        // callSuccess is the single source of truth. The previous "errorMessage only" check
        // had two problems: it falsely reported success when the backend returned
        // {callSuccess:"0", errorMessage:"null"} (string), and could falsely report errors
        // when callSuccess === "1" arrived with a non-fatal warning in errorMessage.
        if (response?.callSuccess === '1' || response?.callSuccess === 1) {
          this.gtwSnackbarService.openSnackBar('Status is saved successfully', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          const msg = (response?.errorMessage && response.errorMessage !== 'null')
            ? response.errorMessage
            : 'An error occurred while saving. Please try again.';
          this.gtwSnackbarService.openSnackBar(msg, 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  removeEntity(index: number) {
    this.entities.splice(index, 1);
    if (this.entities.length === 0) {
      this.cancel();
    }
  }

  openCalendar(index: number) {
    const calSet = this.objects.length + 1;
    for (let i = calSet - 1; i >= 0; i--) {
      if (i === index) {
        this.calendar.opened[i] = true;
        break;
      } else {
        this.calendar.opened[i] = false;
      }
    }
  }

  openEditCalendar(index: number) {
    const calSetEdit = this.editRecords.length + 1;
    for (let i = calSetEdit - 1; i >= 0; i--) {
      if (i === index) {
        this.editcalendar.opened[i] = true;
        break;
      } else {
        this.editcalendar.opened[i] = false;
      }
    }
  }

  formatDate(dateIndex: number) {
    for (let i = 0; i < this.objects.length; i++) {
      // Handle different date formats: Date object, string, or object from date picker
      let dateValue = this.objects[i].dateFiled;
      
      // Convert object to Date if needed (date picker might return an object)
      if (dateValue && typeof dateValue === 'object' && !(dateValue instanceof Date)) {
        // Try to extract date from object (common date picker formats)
        if (dateValue.getTime) {
          dateValue = new Date(dateValue.getTime());
        } else if (dateValue.year && dateValue.month && dateValue.day) {
          // Handle {year, month, day} format
          dateValue = new Date(dateValue.year, dateValue.month - 1, dateValue.day);
        } else if (dateValue.value) {
          // Handle {value: Date} format
          dateValue = dateValue.value instanceof Date ? dateValue.value : new Date(dateValue.value);
        } else {
          // If can't convert, skip this iteration
          dateValue = null;
        }
      } else if (dateValue && typeof dateValue === 'string') {
        // Convert string to Date
        dateValue = new Date(dateValue);
      }
      
      // Only process if dateValue is a valid Date object
      let dateYear: string | null = null;
      if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
        dateYear = this.datePipe.transform(dateValue, 'yyyy');
      }
      
      const endTaxYear = this.rowData.END_TAX_YEAR;
      const taxYear = this.rowData.TAX_YEAR;
      
      // Check if dateFiled is defined and not empty (matches original JS line 351)
      const isDateDefined = this.objects[i].dateFiled != null && this.objects[i].dateFiled != '';
      
      if (dateYear && parseInt(dateYear) < parseInt(endTaxYear) && 
          isDateDefined && this.statusFlag == true) {
        this.objects[i].invalidDateFlag = true;
      } else if (dateYear && parseInt(dateYear) < parseInt(taxYear) && isDateDefined) {
        this.objects[i].invalidDateOpenFlag = true;
      } else {
        this.objects[i].invalidDateFlag = false;
        this.objects[i].invalidDateOpenFlag = false;
      }
    }
    
    // Set dateFlag to false for the changed date (matches original JS line 363)
    if (this.objects[dateIndex]) {
      this.objects[dateIndex].dateFlag = false;
    }
  }

  validateAdjustment() {
    for (let i = 0; i < this.objects.length; i++) {
      if (((this.objects[i].descriptionDesc && this.objects[i].descriptionDesc != null && this.objects[i].descriptionDesc != "") ||
           (this.objects[i].dateFiled && this.objects[i].dateFiled != null && this.objects[i].dateFiled != '') ||
           (this.objects[i].taxesAmount && this.objects[i].taxesAmount != null && this.objects[i].taxesAmount != '') ||
           (this.objects[i].descriptionDateProvidedDesc && this.objects[i].descriptionDateProvidedDesc != null && this.objects[i].descriptionDateProvidedDesc != '') ||
           (this.objects[i].ftrRelatesToDesc && this.objects[i].ftrRelatesToDesc != null && this.objects[i].ftrRelatesToDesc != '')) &&
          this.rowData.STATUS == 'Closed' &&
          (!this.objects[i].adjustmentDesc || this.objects[i].adjustmentDesc == null || this.objects[i].adjustmentDesc == '')) {
        this.objects[i].adjustFlag = true;
      }
    }
  }

  validateDescription() {
    for (let i = 0; i < this.objects.length; i++) {
      if (((this.objects[i].adjustmentDesc && this.objects[i].adjustmentDesc != null && this.objects[i].adjustmentDesc != "") ||
           (this.objects[i].dateFiled && this.objects[i].dateFiled != null && this.objects[i].dateFiled != '') ||
           (this.objects[i].taxesAmount && this.objects[i].taxesAmount != null && this.objects[i].taxesAmount != '') ||
           (this.objects[i].descriptionDateProvidedDesc && this.objects[i].descriptionDateProvidedDesc != null && this.objects[i].descriptionDateProvidedDesc != '') ||
           (this.objects[i].ftrRelatesToDesc && this.objects[i].ftrRelatesToDesc != null && this.objects[i].ftrRelatesToDesc != '')) &&
          (!this.objects[i].descriptionDesc || this.objects[i].descriptionDesc == null || this.objects[i].descriptionDesc == '')) {
        this.objects[i].descFlag = true;
      }
    }
  }

  validateDate() {
    for (let i = 0; i < this.objects.length; i++) {
      if (((this.objects[i].adjustmentDesc && this.objects[i].adjustmentDesc != null && this.objects[i].adjustmentDesc != "") ||
           (this.objects[i].descriptionDesc && this.objects[i].descriptionDesc != null && this.objects[i].descriptionDesc != '') ||
           (this.objects[i].taxesAmount && this.objects[i].taxesAmount != null && this.objects[i].taxesAmount != '') ||
           (this.objects[i].descriptionDateProvidedDesc && this.objects[i].descriptionDateProvidedDesc != null && this.objects[i].descriptionDateProvidedDesc != '') ||
           (this.objects[i].ftrRelatesToDesc && this.objects[i].ftrRelatesToDesc != null && this.objects[i].ftrRelatesToDesc != '')) &&
          this.rowData.STATUS == 'Closed' &&
          (!this.objects[i].dateFiled || this.objects[i].dateFiled == null || this.objects[i].dateFiled == '')) {
        this.objects[i].dateFlag = true;
      }
    }
  }

  validateValues() {
    for (let i = 0; i < this.objects.length; i++) {
      if (((this.objects[i].adjustmentDesc && this.objects[i].adjustmentDesc != null && this.objects[i].adjustmentDesc != "") ||
           (this.objects[i].descriptionDesc && this.objects[i].descriptionDesc != null && this.objects[i].descriptionDesc != '') ||
           (this.objects[i].taxesAmount && this.objects[i].taxesAmount != null && this.objects[i].taxesAmount != '') ||
           (this.objects[i].dateFiled && this.objects[i].dateFiled != null && this.objects[i].dateFiled != '') ||
           (this.objects[i].descriptionDateProvidedDesc && this.objects[i].descriptionDateProvidedDesc != null && this.objects[i].descriptionDateProvidedDesc != '')) &&
          (!this.objects[i].ftrRelatesToDesc || this.objects[i].ftrRelatesToDesc == null || this.objects[i].ftrRelatesToDesc == "")) {
        this.objects[i].ftrRelatestodescFlag = true;
      }
      
      if (((this.objects[i].adjustmentDesc && this.objects[i].adjustmentDesc != null && this.objects[i].adjustmentDesc != "") ||
           (this.objects[i].descriptionDesc && this.objects[i].descriptionDesc != null && this.objects[i].descriptionDesc != '') ||
           (this.objects[i].taxesAmount && this.objects[i].taxesAmount != null && this.objects[i].taxesAmount != '') ||
           (this.objects[i].dateFiled && this.objects[i].dateFiled != null && this.objects[i].dateFiled != '') ||
           (this.objects[i].ftrRelatesToDesc && this.objects[i].ftrRelatesToDesc != null && this.objects[i].ftrRelatesToDesc != '')) &&
          (!this.objects[i].descriptionDateProvidedDesc || this.objects[i].descriptionDateProvidedDesc == null || this.objects[i].descriptionDateProvidedDesc == "")) {
        this.objects[i].descriptionDateProvidedFlag = true;
      }
    }
  }

  changeDescriptionLaws(value: any, value3: number) {
    const desctVariable = value3;
    if (this.objects[desctVariable]) {
      this.objects[desctVariable].descFlag = false;
    }
  }

  changeAdjustmentLaws(value1: any, value2: number) {
    const adjustVariable = value2;
    if (this.objects[adjustVariable]) {
      this.objects[adjustVariable].adjustFlag = false;
    }
  }

  changeFTRDescription(value: any, value3: number) {
    const desctVariable = value3;
    if (this.objects[desctVariable]) {
      this.objects[desctVariable].ftrRelatestodescFlag = false;
    }
  }

  changeDateProvidedDescription(value: any, value3: number) {
    const desctVariable = value3;
    if (this.objects[desctVariable]) {
      this.objects[desctVariable].descriptionDateProvidedFlag = false;
    }
  }

  updated(currencyValue: string) {
    this.selectedGroup = this.getCurrencyDisplayName(currencyValue);
  }

  getCurrencyDisplayName(currencyValue: string) {
    let tempObj: any = {};
    for (let i = 0; i < this.currencies.length; i++) {
      if (this.currencies[i].CURR_CODE === currencyValue) {
        tempObj = this.currencies[i];
        break;
      }
    }
    return tempObj;
  }

  addNew(row: any) {
    const rowObj: any = {};
    const rowFlag = 'New';
    // Use filterLength when rowFlag is not "New" (matches original JS line 690)
    rowObj.id = rowFlag == "New" ? (this.objects.length > 0 ? this.objects[this.objects.length - 1].id + 1 : 1) : this.filterLength;
    rowObj.tax_year = this.rowData.TAX_YEAR;
    rowObj.combination_key = this.rowData.COMBINATION_KEY;
    rowObj.descriptionDesc = null; // Set to null to match [ngValue]="null" in dropdown placeholder
    rowObj.description = [];
    rowObj.adjustmentDesc = null; // Set to null to match [ngValue]="null" in dropdown placeholder - shows "Select Type of Adjustment" by default
    rowObj.adjustment = [];
    rowObj.dateFiled = '';
    rowObj.dateFiledDates = [];
    rowObj.isEdit = false;
    rowObj.taxesAmount = 0;
    rowObj.dateUpdate = '';
    rowObj.userUpdate = '';
    rowObj.usrcomments = '';
    rowObj.transDetailsKey = 0;
    rowObj.adjustFlag = false;
    rowObj.descFalg = false;
    rowObj.dateFlag = false;
    rowObj.invalidDateFlag = false;
    rowObj.invalidDateOpenFlag = false;
    rowObj.ftrRelatesToDesc = null; // Set to null to match [ngValue]="null" in dropdown placeholder
    rowObj.descriptionDateProvidedDesc = null; // Set to null to match [ngValue]="null" in dropdown placeholder
    rowObj.ftrRelatestodescFlag = false;
    rowObj.descriptionDateProvidedFlag = false;
    this.objects.push(rowObj);
    return rowObj;
  }

  reset() {
    // 🔴 CRITICAL: Restore from original data instead of reloading from API
    // This preserves dropdown values that were properly mapped when modal opened
    // Pattern matches F7205Component, F1120pcSchEComponent, IssueEditComponent
    if (this.originalEditRecords && this.originalEditRecords.length > 0) {
      // Restore editRecords from original data (deep copy to avoid reference issues)
      this.editRecords = _.cloneDeep(this.originalEditRecords);
      
      // Also reset the first table (objects) if needed
      if (this.OnLoadObjData && this.OnLoadObjData.length > 0) {
        this.objects = _.cloneDeep(this.OnLoadObjData);
      }
      this.syncDatePickerArrays();
      
      // Refresh filtered records to update the UI
      this.refreshFilteredRecords();
      
      // Trigger change detection to update dropdowns
      this.cdr.detectChanges();
    } else {
      // Fallback: If no original data stored, reload from API (original behavior)
      this.resetFlag = true;
      this.getModelPopup();
    }
  }

  editLiab(editData: any, isDelete: boolean, index: number) {
    // Store edit parameters for use after confirmation
    this.pendingEditParams = { editData, isDelete, index };
    
    // Show confirmation dialog (matches admin pattern)
    this.modalConfig = {
      title: 'Edit Tax Liability?',
      message: 'Are you sure you want to edit this Tax Liability?',
      iconClass: 'fa fa-exclamation-triangle',
      iconColor: 'txt-color-orange-dark',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      confirmButtonClass: 'btn-danger',
      cancelButtonClass: 'btn-default',
      showCancelButton: true,
      showConfirmButton: true,
      animationClass: 'animated fadeIn fast',
      loading: false,
      loadingText: 'Processing...'
    };
    this.showConfirmDialog = true;
    this.cdr.detectChanges();
  }
  
  onConfirmEdit(): void {
    this.showConfirmDialog = false;
    
    if (!this.pendingEditParams) {
      return;
    }
    
    const { editData, isDelete, index } = this.pendingEditParams;
    this.pendingEditParams = null;
    
    // Perform the actual edit operation
    this.performEdit(editData, isDelete, index);
  }
  
  onCancelConfirm(): void {
    this.showConfirmDialog = false;
    this.pendingDeleteParams = null;
    this.pendingEditParams = null;
    this.cdr.detectChanges();
  }
  
  private performEdit(editData: any, isDelete: boolean, index: number): void {
    if (this.editRecords[index]) {
      // 🔴 CRITICAL: Format TRANS_DATE BEFORE changing EDIT_FLAG
      // This ensures the date is ready when the date picker is rendered
      if (this.editRecords[index].TRANS_DATE) {
        let dateValue = this.editRecords[index].TRANS_DATE;
        
        // Convert to Date object if it's a string
        // 🔴 CRITICAL: Parse as local date to avoid timezone shift
        if (typeof dateValue === 'string') {
          // Handle different string formats
          if (dateValue.match(/^\d{4}-\d{2}-\d{2}/)) {
            // YYYY-MM-DD format - parse as local date
            const parts = dateValue.split('-');
            dateValue = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
          } else if (dateValue.includes('/')) {
            // MM/DD/YYYY format - parse as local date
            const parts = dateValue.split('/');
            dateValue = new Date(parseInt(parts[2]), parseInt(parts[0]) - 1, parseInt(parts[1]));
          } else {
            // Try generic Date parsing, then use local components to avoid timezone shift
            const tempDate = new Date(dateValue);
            if (!isNaN(tempDate.getTime())) {
              dateValue = new Date(tempDate.getFullYear(), tempDate.getMonth(), tempDate.getDate());
            } else {
              dateValue = tempDate;
            }
          }
        } else if (dateValue && typeof dateValue === 'object' && !(dateValue instanceof Date)) {
          // Handle date picker object format
          if (dateValue.year && dateValue.month && dateValue.day) {
            dateValue = new Date(dateValue.year, dateValue.month - 1, dateValue.day);
          } else if (dateValue.getTime) {
            // Use local date components to avoid timezone shift
            const tempDate = new Date(dateValue.getTime());
            dateValue = new Date(tempDate.getFullYear(), tempDate.getMonth(), tempDate.getDate());
          }
        } else if (dateValue instanceof Date) {
          // Use local date components to avoid timezone shift
          dateValue = new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate());
        }
        
        // Only assign if it's a valid Date object
        if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
          this.editRecords[index].TRANS_DATE = dateValue;
          this.editRecords[index].transDateDates = this.toDateArray(dateValue);
        } else {
          // If invalid, set to null so date picker can handle it
          this.editRecords[index].TRANS_DATE = null;
          this.editRecords[index].transDateDates = [];
        }
      } else {
        this.editRecords[index].transDateDates = [];
      }
      
      // Now set EDIT_FLAG to false (this will trigger *ngIf and render date picker)
      this.editRecords[index].EDIT_FLAG = false;
      
      // Trigger change detection to update the date picker display
      // Use setTimeout to ensure date picker component is fully rendered before change detection
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 0);
    }
  }

  singleRowDelete(response: any, isDelete: boolean, index: number) {
    // Store delete parameters for use after confirmation
    this.pendingDeleteParams = { response, isDelete, index };
    
    // Show confirmation dialog (matches admin pattern)
    this.modalConfig = {
      title: 'Delete Tax Liability',
      message: 'Are you sure you want to permanently delete this Tax Liability?',
      iconClass: 'fa fa-exclamation-triangle',
      iconColor: 'txt-color-orange-dark',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      confirmButtonClass: 'btn-danger',
      cancelButtonClass: 'btn-default',
      showCancelButton: true,
      showConfirmButton: true,
      animationClass: 'animated fadeIn fast',
      loading: false,
      loadingText: 'Deleting...'
    };
    this.showConfirmDialog = true;
    this.cdr.detectChanges();
  }
  
  onConfirmDelete(): void {
    this.showConfirmDialog = false;
    
    if (!this.pendingDeleteParams) {
      return;
    }
    
    const { response, isDelete, index } = this.pendingDeleteParams;
    this.pendingDeleteParams = null;
    
    // Perform the actual delete operation
    this.performDelete(response, isDelete, index);
  }
  
  private performDelete(response: any, isDelete: boolean, index: number): void {
    const resDelObj: any = {};
    if (this.editRecords[index]) {
      const res = this.editRecords[index];
      resDelObj.trans_details_key = res.TRANS_DETAILS_KEY;
      resDelObj.trans_header_key = res.TRANS_HEADER_KEY;
      resDelObj.combination_key = this.rowData.COMBINATION_KEY;
      resDelObj.description = res.DESCRIPTION;
      
      this.taxLiabilityDashboardService.delete(
        resDelObj,
        isDelete,
        this.getBaseUrl(),
        this.globalService,
        this.filterParams,
        this.rowData
      ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (res: any) => {
          // callSuccess is the single source of truth for delete status. errorMessage may
          // carry a non-fatal warning even on a successful delete (callSuccess === "1");
          // see singleRowSave() above for the same rationale.
          if (res?.callSuccess === '1' || res?.callSuccess === 1) {
            this.gtwSnackbarService.openSnackBar('Tax Liability deleted successfully.', 'gtw-snackbar--green');
            this.requestGridUpdate.emit('refresh');
          } else {
            const msg = (res?.errorMessage && res.errorMessage !== 'null')
              ? res.errorMessage
              : 'An error occurred while deleting. Please try again.';
            this.gtwSnackbarService.openSnackBar(msg, 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        (error: any) => {
          console.error('Error deleting record:', error);
          this.gtwSnackbarService.openSnackBar('An error occurred while deleting. Please try again.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      );
    }
  }

  singleRowSave() {
    const resObj: any[] = [];
    const saveObject: any[] = [];
    this.resetFlag = true;
    this.editList = [];
    let edit_id = 100;
    
    const scheduleSettings = [{
      "tax_year": this.filterParams?.tax_year || this.globalService?.tax_year,
      "scenario": this.filterParams?.scenario || this.globalService?.scenario,
      "jcd_key": this.rowData.JCD_KEY || this.globalService?.jcd_key || 250
    }];
    
    this.flag1 = !this.selectedGroup.CURR_CODE || this.selectedGroup.CURR_CODE == "" || this.selectedGroup.CURR_CODE == null;
    if (this.flag1 == true) {
      return;
    }
    
    if (this.editRecords.length) {
      const editRow = _.filter(this.editRecords, (p: any) => {
        return _.includes([false], p.EDIT_FLAG);
      });
      
      for (let i = 0; i < editRow.length; i++) {
        const tempObj = {
          tax_year: this.rowData.TAX_YEAR,
          combination_key: this.rowData.COMBINATION_KEY,
          description: editRow[i].DESCRIPTION,
          adjustment: editRow[i].ADJUSTMENT,
          dateFiled: editRow[i].TRANS_DATE != null ? editRow[i].TRANS_DATE : null,
          trans_type_key: editRow[i].TRANS_TYPE_KEY,
          taxesAmount: editRow[i].FC_AMT,
          usrcomments: editRow[i].DETAIL_COMMENTS,
          transDetailsKey: editRow[i].TRANS_DETAILS_KEY,
          curr_desc: editRow[i].CURRENCY,
          group_id: this.rowData.GROUP_ID == 0 ? '' : this.rowData.GROUP_ID,
          isEdit: true,
          id: edit_id + i,
          ftrDescription: editRow[i].FTRDESCRIPTION,
          ftrRelatesToDesc: editRow[i].FTRRELATESTODESC || null,
          descriptionDateProvidedDesc: editRow[i].DESCRIPTIONDATEPROVIDEDDESC || null
        };
        this.editList.push(tempObj);
      }
    }
    
    if (this.objects.length) {
      this.validateDescription();
      this.validateAdjustment();
      this.validateDate();
      this.validateValues();
      
      for (let i = 0; i < this.objects.length; i++) {
        if (this.objects[i].descFlag == true) {
          this.descRowFlag = true;
          break;
        } else {
          this.descRowFlag = false;
        }
      }
      
      for (let i = 0; i < this.objects.length; i++) {
        if (this.objects[i].adjustFlag == true) {
          this.adjustRowFlag = true;
          break;
        } else {
          this.adjustRowFlag = false;
        }
      }
      
      for (let i = 0; i < this.objects.length; i++) {
        if (this.objects[i].dateFlag == true || this.objects[i].invalidDateFlag == true) {
          this.dateRowFlag = true;
          break;
        } else {
          this.dateRowFlag = false;
        }
      }
      
      for (let i = 0; i < this.objects.length; i++) {
        if (this.objects[i].invalidDateOpenFlag == true) {
          this.dateFlagOpen = true;
          break;
        } else {
          this.dateFlagOpen = false;
        }
      }
      
      if (this.descRowFlag == true || this.adjustRowFlag == true || this.dateRowFlag == true || this.dateFlagOpen == true) {
        return;
      }
      
      const saveObjectData = _.clone(this.objects);
      
      if (this.editList.length) {
        this.editList.forEach((currentObj: any) => {
          const index = _.findIndex(this.objects, { 'id': currentObj.id });
          if (index == -1) {
            saveObjectData.push(currentObj);
          }
        });
      }
      
      for (let i = 0; i < saveObjectData.length; i++) {
        // Handle different date formats: Date object, string, or object from date picker
        let dateValue = saveObjectData[i].dateFiled;
        
        // Convert object to Date if needed (date picker might return an object)
        if (dateValue && typeof dateValue === 'object' && !(dateValue instanceof Date)) {
          // Try to extract date from object (common date picker formats)
          if (dateValue.getTime) {
            dateValue = new Date(dateValue.getTime());
          } else if (dateValue.year && dateValue.month && dateValue.day) {
            // Handle {year, month, day} format
            dateValue = new Date(dateValue.year, dateValue.month - 1, dateValue.day);
          } else if (dateValue.value) {
            // Handle {value: Date} format
            dateValue = dateValue.value instanceof Date ? dateValue.value : new Date(dateValue.value);
          } else {
            // If can't convert, set to null
            dateValue = null;
          }
        } else if (dateValue && typeof dateValue === 'string') {
          // Convert string to Date
          dateValue = new Date(dateValue);
        }
        
        // Only format if dateValue is a valid Date object
        const formattedDate = (dateValue instanceof Date && !isNaN(dateValue.getTime())) ? 
          this.datePipe.transform(dateValue, 'MM/dd/yyyy') : null;
        
        // 🔴 CRITICAL FIX: Create a new object for each iteration and push directly to resObj
        // Instead of reusing saveObject array which causes duplicates
        const saveItem = {
          id: saveObjectData[i].id,
          tax_year: this.rowData.TAX_YEAR,
          combination_key: this.rowData.COMBINATION_KEY,
          description: saveObjectData[i].isEdit == false ? 
            (saveObjectData[i].descriptionDesc?.CODE_DESC || saveObjectData[i].descriptionDesc) : 
            saveObjectData[i].description,
          adjustment: saveObjectData[i].isEdit == false ? 
            (saveObjectData[i].adjustmentDesc?.CODE_DESC || saveObjectData[i].adjustmentDesc) : 
            saveObjectData[i].adjustment,
          dateFiled: formattedDate,
          trans_type_key: saveObjectData[i].isEdit == false ? 
            (saveObjectData[i].descriptionDesc?.CODE || saveObjectData[i].descriptionDesc) : 
            saveObjectData[i].trans_type_key,
          amt: Number(saveObjectData[i].taxesAmount),
          comments: saveObjectData[i].usrcomments,
          trans_details_key: saveObjectData[i].transDetailsKey,
          curr_desc: this.selectedGroup.CURR_CODE,
          group_id: this.rowData.GROUP_ID == 0 ? '' : this.rowData.GROUP_ID,
          ftrDescription: saveObjectData[i].ftrDescription,
          ftrRelatesToDesc: saveObjectData[i].ftrRelatesToDesc?.CODE_DESC || saveObjectData[i].ftrRelatesToDesc,
          descriptionDateProvidedDesc: saveObjectData[i].descriptionDateProvidedDesc?.CODE_DESC || saveObjectData[i].descriptionDateProvidedDesc
        };
        
        // Push the individual object directly into resObj (not the entire saveObject array)
        resObj.push(saveItem);
      }
    }
    
    this.crudLoading = true;
    this.isSaveClicked = true;
    this.cdr.detectChanges();
    
    this.taxLiabilityDashboardService.save(
      resObj,
      scheduleSettings,
      this.getBaseUrl(),
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;

        // callSuccess is the single source of truth for save status. The backend can populate
        // errorMessage with non-fatal warnings/info even when the save succeeded
        // (callSuccess === "1"); treating that as an error caused regression where users saw
        // a red error popup despite the data being saved correctly. Mirrors the canonical
        // pattern in grid-save-built-in-handlers.service.ts wrapSave().
        if (res?.callSuccess === '1' || res?.callSuccess === 1) {
          this.gtwSnackbarService.openSnackBar('Data saved Successfully.', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          const msg = (res?.errorMessage && res.errorMessage !== 'null')
            ? res.errorMessage
            : 'An error has occurred. If this error continues please go to the help page and contact support';
          this.gtwSnackbarService.openSnackBar(msg, 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        this.gtwSnackbarService.openSnackBar('An error has occurred. If this error continues please go to the help page and contact support', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  // Getter for filtered records - only calculates when data is initialized
  get filteredRecords(): any[] {
    // Only return filtered records if data is initialized (editRecords has been populated)
    if (!this.editRecords || this.editRecords.length === 0) {
      return [];
    }
    
    if (!this.rowData || !this.rowData.COMBINATION_KEY) {
      return this.editRecords;
    }
    
    // Calculate filtered records based on combination_key
    return this.editRecords.filter((record: any) => {
      return record.COMBINATION_KEY == this.rowData.COMBINATION_KEY;
    });
  }

  // Getter for filtered total records - only calculates when data is initialized
  get filteredTotalRecords(): any[] {
    // Only return filtered records if data is initialized (editTotalRecords has been populated)
    if (!this.editTotalRecords || this.editTotalRecords.length === 0) {
      return [];
    }
    
    if (!this.rowData || !this.rowData.COMBINATION_KEY) {
      return this.editTotalRecords;
    }
    
    // Calculate filtered records based on combination_key
    return this.editTotalRecords.filter((record: any) => {
      return record.COMBINATION_KEY == this.rowData.COMBINATION_KEY;
    });
  }
  
  // Method to refresh filtered records when data changes - triggers change detection
  private refreshFilteredRecords(): void {
    // Clear cache to force recalculation
    this._filteredRecords = [];
    this._filteredTotalRecords = [];
    // Mark for check to trigger change detection and rerender
    this.cdr.markForCheck();
  }

  /** AppKit dpSelectedDates helper — [date, date] avoids range-within highlight. */
  toDateArray(value: any): Date[] {
    const date = this.parseLocalDate(value);
    return date ? [date, date] : [];
  }

  private parseLocalDate(dateValue: any): Date | null {
    if (!dateValue) {
      return null;
    }

    let date: Date;
    if (dateValue instanceof Date) {
      date = new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate());
    } else if (typeof dateValue === 'string') {
      if (dateValue.match(/^\d{2}\/\d{2}\/\d{4}/)) {
        const parts = dateValue.split('/');
        date = new Date(parseInt(parts[2], 10), parseInt(parts[0], 10) - 1, parseInt(parts[1], 10));
      } else if (dateValue.match(/^\d{4}-\d{2}-\d{2}/)) {
        const parts = dateValue.split('-');
        date = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
      } else {
        const tempDate = new Date(dateValue);
        if (isNaN(tempDate.getTime())) {
          return null;
        }
        date = new Date(tempDate.getFullYear(), tempDate.getMonth(), tempDate.getDate());
      }
    } else if (typeof dateValue === 'object' && dateValue.year && dateValue.month && dateValue.day) {
      date = new Date(dateValue.year, dateValue.month - 1, dateValue.day);
    } else {
      return null;
    }

    return isNaN(date.getTime()) ? null : date;
  }

  onAppkitDateChange(record: any, index: number, selectedDates: Date[]): void {
    const selected = Array.isArray(selectedDates) && selectedDates.length > 0 ? selectedDates[0] : null;
    const date = this.parseLocalDate(selected);
    record.TRANS_DATE = date;
    record.transDateDates = date ? [date, date] : [];
    this.formatDate(index);
    this.cdr.markForCheck();
  }

  onAppkitDateChangeForFirstTable(obj: any, index: number, selectedDates: Date[]): void {
    const selected = Array.isArray(selectedDates) && selectedDates.length > 0 ? selectedDates[0] : null;
    const date = this.parseLocalDate(selected);
    obj.dateFiled = date;
    obj.dateFiledDates = date ? [date, date] : [];
    this.formatDate(index);
    this.cdr.markForCheck();
  }

}

